import { Check, Clock, Package, CreditCard, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "@/hooks/useTranslations";

interface OrderStep {
  id: string;
  label: string;
  date: string | null;
  completed: boolean;
  icon: React.ReactNode;
}

interface OrderTrackerProps {
  createdAt: string;
  paidAt: string | null;
  availableAt: string | null;
  pickedUpAt: string | null;
  status: string;
}

export const OrderTracker = ({ 
  createdAt, 
  paidAt, 
  availableAt, 
  pickedUpAt,
  status
}: OrderTrackerProps) => {
  const { t } = useTranslations();

  // Don't show tracker for cancelled or expired orders
  if (status === 'CANCELLED' || status === 'EXPIRED') {
    return null;
  }

  const steps: OrderStep[] = [
    {
      id: 'created',
      label: t('orders.tracker.created', 'Commande passée'),
      date: createdAt,
      completed: true,
      icon: <Package className="h-5 w-5" />
    },
    {
      id: 'paid',
      label: t('orders.tracker.paid', 'Paiement confirmé'),
      date: paidAt,
      completed: !!paidAt,
      icon: <CreditCard className="h-5 w-5" />
    },
    {
      id: 'available',
      label: t('orders.tracker.available', 'Disponible au retrait'),
      date: availableAt,
      completed: !!availableAt,
      icon: <MapPin className="h-5 w-5" />
    },
    {
      id: 'picked_up',
      label: t('orders.tracker.picked_up', 'Récupérée'),
      date: pickedUpAt,
      completed: !!pickedUpAt,
      icon: <Check className="h-5 w-5" />
    }
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="relative space-y-8">
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isNext = !step.completed && steps[index - 1]?.completed;
        
        return (
          <div key={step.id} className="relative">
            {/* Connecting line */}
            {!isLast && (
              <div 
                className={cn(
                  "absolute left-[23px] top-12 w-0.5 h-16 -translate-x-1/2",
                  step.completed ? "bg-primary" : "bg-border"
                )}
              />
            )}
            
            {/* Step content */}
            <div className="flex items-start gap-4">
              {/* Icon circle */}
              <div 
                className={cn(
                  "relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300",
                  step.completed && "bg-primary border-primary text-primary-foreground shadow-lg shadow-primary/20",
                  isNext && "bg-background border-primary text-primary animate-pulse",
                  !step.completed && !isNext && "bg-background border-border text-muted-foreground"
                )}
              >
                {step.completed ? (
                  <Check className="h-6 w-6" />
                ) : isNext ? (
                  <Clock className="h-5 w-5" />
                ) : (
                  step.icon
                )}
              </div>

              {/* Text content */}
              <div className="flex-1 pt-2">
                <h3 
                  className={cn(
                    "font-semibold text-lg mb-1 transition-colors",
                    step.completed && "text-foreground",
                    isNext && "text-primary",
                    !step.completed && !isNext && "text-muted-foreground"
                  )}
                >
                  {step.label}
                </h3>
                {step.date && (
                  <p className="text-sm text-muted-foreground">
                    {formatDate(step.date)}
                  </p>
                )}
                {isNext && !step.date && (
                  <p className="text-sm text-primary font-medium">
                    {t('orders.tracker.in_progress', 'En cours...')}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
